import React, { useEffect, useState } from "react";
import { AiOutlineClose, AiOutlineSend, AiOutlineMail, AiOutlinePhone, AiOutlineEdit } from "react-icons/ai";
import { apiClient } from "../../../api";
import { toast } from "react-toastify";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { Loader2, Smartphone, CheckCircle2 } from "lucide-react";

const QRScannerModal = ({ onClose, onScanSuccess, propertyDetails }) => {
    const { selectedPropertyId } = useProperty();
    const [isEditing, setIsEditing] = useState(false);
    
    // Get the most up-to-date data from localStorage if available, otherwise use props
    const getInitialData = () => {
        try {
            const storedProperties = JSON.parse(localStorage.getItem("propertiesList") || "[]");
            const fresh = storedProperties.find(p => String(p._id) === String(selectedPropertyId));
            return fresh || propertyDetails;
        } catch (e) {
            return propertyDetails;
        }
    };

    const initialData = getInitialData();
    const [email, setEmail] = useState(initialData?.email || "");
    const [phone, setPhone] = useState(initialData?.phoneNumber || "");
    const [countryCode, setCountryCode] = useState(initialData?.countryCode || "+1");
    const [hasManuallyEdited, setHasManuallyEdited] = useState(false);
    const [isSendingLink, setIsSendingLink] = useState(false);
    const [linkSentSuccess, setLinkSentSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchProperty = async () => {
            if (!selectedPropertyId) return;
            try {
                setIsLoading(true);
                const response = await apiClient.get(`/property/${selectedPropertyId}`);
                const propData = response.data.result.property.property;
                if (propData) {
                    // Update Local Storage for future instant loads
                    const storedPropertiesStr = localStorage.getItem("propertiesList");
                    if (storedPropertiesStr) {
                        const storedProperties = JSON.parse(storedPropertiesStr);
                        const index = storedProperties.findIndex(p => String(p._id) === String(selectedPropertyId));
                        
                        if (index !== -1) {
                            const current = storedProperties[index];
                            const hasChanged = 
                                current.email !== propData.email || 
                                current.phoneNumber !== propData.phoneNumber || 
                                current.countryCode !== (propData.countryCode || "+1");

                            if (hasChanged) {
                                storedProperties[index] = {
                                    ...current,
                                    email: propData.email || current.email,
                                    phoneNumber: propData.phoneNumber || current.phoneNumber,
                                    countryCode: propData.countryCode || current.countryCode || "+1"
                                };
                                localStorage.setItem("propertiesList", JSON.stringify(storedProperties));
                                console.log("Updated localStorage with fresh property details");
                            }
                        }
                    }

                    if (!hasManuallyEdited) {
                        setEmail(propData.email || "");
                        setPhone(propData.phoneNumber || "");
                        setCountryCode(propData.countryCode || "+1");
                    }
                }
            } catch (err) {
                console.error("Failed to fetch property details:", err);
            } finally {
                setIsLoading(false);
            }
        };

        // Reset the manual edit flag when switching properties
        setHasManuallyEdited(false);
        fetchProperty();
    }, [selectedPropertyId]);

    const normalizePhone = (input) => {
        if (!input) return "";
        return String(input).replace(/[^\d]/g, "");
    };

    const handleSendLink = async () => {
        if (!email && !phone) {
            toast.error("Please provide either an email or phone number.");
            return;
        }

        setIsSendingLink(true);
        try {
            const response = await apiClient.post("/api/qrs/request-linking-link", {
                propertyId: selectedPropertyId,
                email: email.trim(),
                phone: normalizePhone(phone),
                countryCode
            });

            if (response.status === 200) {
                const { emailSent, smsSent, errors } = response.data.result;
                
                if (errors && errors.length > 0) {
                    if (emailSent || smsSent) {
                        toast.success("Link sent");
                        errors.forEach(err => toast.warn(err));
                        setLinkSentSuccess(true);
                    } else {
                        errors.forEach(err => toast.error(err));
                    }
                } else {
                    toast.success("Link sent");
                    setLinkSentSuccess(true);
                }

                if (emailSent || smsSent) {
                    setTimeout(() => {
                        onClose();
                    }, 4000);
                }
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Failed to send linking link.";
            toast.error(errorMsg);
        } finally {
            setIsSendingLink(false);
        }
    };

    // Removed handleFileChange

    return (
        <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full relative animate-in fade-in zoom-in duration-300 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-8 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    >
                        <AiOutlineClose className="h-5 w-5 text-white" />
                    </button>
                    
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md">
                        <Smartphone className="w-8 h-8 text-white" />
                    </div>
                    
                    <h2 className="text-2xl font-bold tracking-tight">Link QR</h2>
                    <p className="text-indigo-100 mt-2 text-sm">Send a linking link to your device to link your QR codes.</p>
                    
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                        <div className="flex items-center gap-3 text-indigo-50/90">
                            <AiOutlineMail className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-medium truncate">{email || 'No email set'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-indigo-50/90">
                            <AiOutlinePhone className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-medium">{phone ? `${countryCode} ${phone}` : 'No phone set'}</span>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    {linkSentSuccess ? (
                        <div className="text-center py-6 animate-in fade-in slide-in-from-bottom-4">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Link Sent!</h3>
                            <p className="text-gray-500 mb-6 font-medium">Check your device for the link.</p>
                            <button
                                onClick={onClose}
                                className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-2xl transition-all"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                                <>
                                    {isEditing ? (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                            <div className="space-y-4">
                                                <div className="group">
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Email Address</label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                            <AiOutlineMail className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                                                        </div>
                                                        <input
                                                            type="email"
                                                            value={email}
                                                            onChange={(e) => {
                                                                setEmail(e.target.value);
                                                                setHasManuallyEdited(true);
                                                            }}
                                                            placeholder="Enter email"
                                                            className="block w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 rounded-2xl text-sm font-medium transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="group">
                                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Phone Number</label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={countryCode}
                                                            onChange={(e) => setCountryCode(e.target.value)}
                                                            className="w-20 px-3 py-4 bg-gray-50 border-transparent focus:border-indigo-500 rounded-2xl text-sm font-medium text-center"
                                                        />
                                                        <div className="relative flex-1">
                                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                                <AiOutlinePhone className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                                                            </div>
                                                            <input
                                                                type="tel"
                                                                value={phone}
                                                                onChange={(e) => {
                                                                    setPhone(e.target.value);
                                                                    setHasManuallyEdited(true);
                                                                }}
                                                                placeholder="Phone number"
                                                                className="block w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl text-sm font-medium transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setIsEditing(false)}
                                                className="w-full py-4 bg-gray-900 text-white font-bold rounded-2xl transition-all hover:bg-gray-800 active:scale-95 mt-2"
                                            >
                                                Done Editing
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className="w-full flex items-center justify-center gap-3 py-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-2xl transition-all active:scale-95 border-2 border-indigo-100 border-dashed"
                                            >
                                                <AiOutlineEdit className="w-6 h-6" />
                                                <span>Change details</span>
                                            </button>

                                            <button
                                                onClick={handleSendLink}
                                                disabled={isSendingLink}
                                                className="w-full flex items-center justify-center gap-3 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-100 active:scale-95"
                                            >
                                                {isSendingLink ? <Loader2 className="w-5 h-5 animate-spin" /> : <AiOutlineSend className="w-5 h-5" />}
                                                <span>Send Link</span>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                </div>
            </div>
        </div>
    );
};

export default QRScannerModal;
